import { Injectable } from '@angular/core';
import { BudBotRepository } from '../repositories/bud-bot/bud-bot-repository';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { BsError } from '../models/shared/bs-error';
import { BehaviorSubject, combineLatest, Observable, of, Subject, withLatestFrom } from 'rxjs';
import { BudBotConversation } from '../models/budbot/bud-bot-conversation';
import { WebSocketMessageContextResponse } from '../models/web-sockets/contexts/web-socket-message-context-response';
import { WebSocketService } from '../services/web-socket.service';
import { WebSocketBudBotResponse } from '../models/web-sockets/messages/receive/web-socket-bud-bot-response';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { ContextForAI, UserQuery } from '@mobilefirstdev/chat-interface';
import { BudBotUserQuery } from '../models/budbot/bud-bot-user-query';
import { exists } from '../functions/exists';
import { BudBotLanguageModelResponse } from '../models/budbot/bud-bot-language-model-response';
import { WebSocketBudBotConversationResponse } from '../models/web-sockets/messages/receive/web-socket-bud-bot-conversation-response';
import { DistinctUtils } from '../utils/distinct-utils';
import { ToastService } from '../modules/toasts/service/toast.service';

type ChatMessage = BudBotUserQuery | BudBotLanguageModelResponse;

// Provided by logged in scope
@Injectable()
export class BudBotDomainModel extends BaseDomainModel {
  constructor(
    private budBotRepository: BudBotRepository,
    private toastService: ToastService,
    private webSocketService: WebSocketService
  ) {
    super();
    this.listenForConversationChange();
    this.listenForWebSocketMessages();
    this.listenForConversationUpdates();
  }

  private readonly _selectedContext = new BehaviorSubject<ContextForAI | null>(null);
  public readonly selectedContext$ = this._selectedContext as Observable<ContextForAI | null>;
  connectToSelectedContext = (context: ContextForAI): void => this._selectedContext.next(context);

  private readonly _selectedConversationId = new BehaviorSubject<string | null>(null);
  public readonly selectedConversationId$ = this._selectedConversationId.pipe(distinctUntilChanged());
  connectToSelectedConversationId = (id: string): void => this._selectedConversationId.next(id);

  private readonly _addOrUpdateConversation = new Subject<BudBotConversation>();
  public readonly addOrUpdateConversation$ = this._addOrUpdateConversation as Observable<BudBotConversation>;
  addOrUpdateConversation = (convo: BudBotConversation): void => {
    if (!convo) return;
    this._addOrUpdateConversation.next(convo);
  };

  public readonly conversations$: Observable<BudBotConversation[] | null> = this.budBotRepository
    .getConversations$()
    .pipe(
      switchMap(conversations => {
        return this.addOrUpdateConversation$.pipe(
          map(incomingConvo => {
            const existingIndex = conversations?.findIndex(c => c?.id === incomingConvo?.id) ?? -1;
            existingIndex > -1
              ? conversations?.splice(existingIndex, 1, incomingConvo)
              : conversations?.unshift(incomingConvo);
            return conversations?.shallowCopy();
          }),
          startWith(conversations)
        );
      }),
      map(conversations => conversations?.shallowCopy() ?? null),
      catchError((error: BsError) => {
        this.toastService.publishError(error);
        return [];
      }),
      startWith(null),
      distinctUntilChanged(DistinctUtils.distinctNulls),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  private readonly _conversationMessages = new BehaviorSubject<Map<string, ChatMessage[]>>(new Map());
  public readonly conversationMessages$ = this._conversationMessages as Observable<Map<string, ChatMessage[]>>;
  updateConversationWithAllMessages = (conversationId: string, messages: ChatMessage[]): void => {
    const currentMessages = this._conversationMessages.getValue();
    const updatedMessages = new Map(currentMessages);
    updatedMessages.set(conversationId, messages);
    this._conversationMessages.next(updatedMessages);
  };

  addToConvoMessages = (m: BudBotUserQuery | WebSocketBudBotResponse): void => {
    const msg = m instanceof WebSocketBudBotResponse ? m?.payload : m;
    const updatedMap = new Map(this._conversationMessages.getValue());
    const convoId = msg?.conversationId;
    if (convoId) {
      const messages = updatedMap?.get(convoId)?.shallowCopy() || [];
      const alreadyHas = messages.find(message => {
        const sameMessages = exists(message?.id) ? message?.id === msg?.id : message?.query === msg?.query;
        return sameMessages && this.messagesAreSameType(message, msg);
      });
      if (exists(alreadyHas)) {
        const index = messages.findIndex(message => message?.id === msg?.id);
        messages.splice(index, 1, msg);
      } else {
        messages.push(msg);
      }
      updatedMap.set(convoId, messages);
      this._conversationMessages.next(updatedMap);
    }
  };

  private messagesAreSameType(a: any, b: any): boolean {
    return a?.constructor === b?.constructor;
  }

  public readonly conversationsWithHydratedMessages$ = combineLatest([
    this.conversations$,
    this.conversationMessages$
  ]).pipe(
    map(([conversations, messages]) => {
      const updated = conversations?.shallowCopy();
      updated?.forEach(conversation => (conversation.messages = messages?.get(conversation.id) || null));
      return updated;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private listenForConversationChange(): void {
    this.selectedConversationId$
      .pipe(
        map(selectedConversationId => selectedConversationId ?? null),
        distinctUntilChanged((prev, curr) => prev === curr),
        withLatestFrom(this.conversations$, this.conversationMessages$),
        concatMap(([conversationId, conversations, messages]) => {
          // the conversation must exist to fetch messages from the backend, otherwise it's a new conversation
          if (!conversationId || !conversations?.find(convo => convo?.id === conversationId)) {
            return of(null);
          } else {
            if (!messages?.get(conversationId)) {
              return this.budBotRepository
                .getConversationMessages$(conversationId)
                .pipe(map(messages => [conversationId, messages]));
            } else {
              return of([conversationId, messages.get(conversationId)]);
            }
          }
        }),
        filter(exists),
        catchError((error: BsError) => {
          this.toastService.publishError(error);
          return [];
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.onDestroy)
      )
      .subscribe({
        next: ([convoId, messages]) => {
          this.updateConversationWithAllMessages(convoId, messages);
        }
      });
  }

  private listenForWebSocketMessages(): void {
    this.webSocketService.getRelevantMessages$([WebSocketMessageContextResponse.BudBot]).subscribeWhileAlive({
      owner: this,
      next: message => {
        if (message instanceof WebSocketBudBotResponse) {
          this.addToConvoMessages(message);
        }
      }
    });
  }

  private listenForConversationUpdates(): void {
    this.webSocketService
      .getRelevantMessages$([WebSocketMessageContextResponse.BudBotConversation])
      .subscribeWhileAlive({
        owner: this,
        next: message => {
          if (message instanceof WebSocketBudBotConversationResponse) {
            this.addOrUpdateConversation(message?.payload);
          }
        }
      });
  }

  public sendMessage(msg: UserQuery): void {
    if (msg instanceof BudBotUserQuery) {
      this.addToConvoMessages(msg);
      this.budBotRepository.askBudBot$(msg).once();
    }
  }
}
